import React, { Fragment, useEffect, useState } from "react";
import { CardBody, Col, Row, Table } from "reactstrap";
import { Link } from "react-router-dom";

import {
  Column,
  Table as ReactTable,
  ColumnFiltersState,
  FilterFn,
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender
} from '@tanstack/react-table';

import { rankItem } from '@tanstack/match-sorter-utils';

// Column Filter
const Filter = ({
  column
}: {
  column: Column<any, unknown>;
  table: ReactTable<any>;
}) => {
  const columnFilterValue = column.getFilterValue();

  return (
    <>
      <DebouncedInput
        type="text"
        value={(columnFilterValue ?? '') as string}
        onChange={value => column.setFilterValue(value)}
        placeholder="Search..."
        className="w-36 border shadow rounded"
        list={column.id + 'list'}
      />
      <div className="h-1" />
    </>
  );
};

// Global Filter
const DebouncedInput = ({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}: {
  value: string | number;
  onChange: (value: string | number) => void;
  debounce?: number;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'>) => {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);

    return () => clearTimeout(timeout);
  }, [debounce, onChange, value]);

  return (
    <input {...props} value={value} id="search-bar-0" className="form-control border-0 search" onChange={e => setValue(e.target.value)} />
  );
};

interface TableContainerProps {
  columns?: any;
  data?: any;
  isGlobalFilter?: any;
  handleTaskClick?: any;
  customPageSize?: any;
  tableClass?: any;
  theadClass?: any;
  trClass?: any;
  iscustomPageSize?: any;
  thClass?: any;
  divClass?: any;
  SearchPlaceholder?: any;
  handleLeadClick?: any;
  handleCompanyClick?: any;
  handleContactClick?: any;
  handleTicketClick?: any;
  isBordered?: any;
  currentPage?: number;
  totalPages?: number;
  totalCount?: number;
  onPageChange?: (page: number) => void;
  isServerSide?: boolean;
}

const TableContainer = ({
  columns,
  data,
  isGlobalFilter,
  customPageSize,
  tableClass,
  theadClass,
  trClass,
  thClass,
  divClass,
  SearchPlaceholder,
  isBordered: _isBordered,
  // Server-side pagination props
  currentPage = 1,
  totalPages = 1,
  totalCount = 0,
  onPageChange,
  isServerSide = false

}: TableContainerProps) => {
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState('');

  const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
    const itemRank = rankItem(row.getValue(columnId), value);
    addMeta({
      itemRank
    });
    return itemRank.passed;
  };

  const table = useReactTable({
    columns,
    data,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    state: {
      columnFilters,
      globalFilter,
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: fuzzyFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    // Only use client-side pagination if not server-side
    ...(isServerSide ? {} : { getPaginationRowModel: getPaginationRowModel() }),
    getSortedRowModel: getSortedRowModel(),
    // Disable pagination for server-side
    ...(isServerSide ? { manualPagination: true } : {})
  });

  const {
    getHeaderGroups,
    getRowModel,
    getCanPreviousPage,
    getCanNextPage,
    getPageOptions,
    setPageIndex,
    nextPage,
    previousPage,
    // setPageSize,
    getState
  } = table;

  // Server-side pagination handlers
  const handlePreviousPage = () => {
    if (isServerSide && onPageChange && currentPage > 1) {
      onPageChange(currentPage - 1);
    } else if (!isServerSide) {
      previousPage();
    }
  };

  const handleNextPage = () => {
    if (isServerSide && onPageChange && currentPage < totalPages) {
      onPageChange(currentPage + 1);
    } else if (!isServerSide) {
      nextPage();
    }
  };

  const handlePageClick = (pageIndex: number) => {
    if (isServerSide && onPageChange) {
      onPageChange(pageIndex + 1); // Convert to 1-based
    } else if (!isServerSide) {
      setPageIndex(pageIndex);
    }
  };

  // Get pagination info based on mode
  const getPaginationInfo = () => {
    if (isServerSide) {
      const startEntry = (currentPage - 1) * (customPageSize || 10) + 1;
      const endEntry = Math.min(currentPage * (customPageSize || 10), totalCount);
      return {
        currentPageSize: Math.min(customPageSize || 10, data.length),
        totalEntries: totalCount,
        canPrevious: currentPage > 1,
        canNext: currentPage < totalPages,
        pageOptions: Array.from({ length: totalPages }, (_, i) => i),
        currentPageIndex: currentPage - 1,
        startEntry,
        endEntry
      };
    } else {
      return {
        currentPageSize: getState().pagination.pageSize,
        totalEntries: data.length,
        canPrevious: getCanPreviousPage(),
        canNext: getCanNextPage(),
        pageOptions: getPageOptions(),
        currentPageIndex: getState().pagination.pageIndex,
        startEntry: getState().pagination.pageIndex * getState().pagination.pageSize + 1,
        endEntry: Math.min((getState().pagination.pageIndex + 1) * getState().pagination.pageSize, data.length)
      };
    }
  };

  const paginationInfo = getPaginationInfo();

  return (
    <Fragment>
      {isGlobalFilter && <Row className="mb-3">
        <CardBody className="border border-dashed border-end-0 border-start-0">
          <form>
            <Row>
              <Col sm={5}>
                <div className="search-box me-2 mb-2 d-inline-block col-12">
                  <DebouncedInput
                    value={globalFilter ?? ''}
                    onChange={value => setGlobalFilter(String(value))}
                    placeholder={SearchPlaceholder}
                  />
                  <i className="bx bx-search-alt search-icon"></i>
                </div>
              </Col>
            </Row>
          </form>
        </CardBody>
      </Row>}


      <div className={divClass}>
        {/* make table horizontally scrollable while keeping pagination/footer fixed */}
        <div className="table-responsive">
          <Table hover className={tableClass}>
          <thead className={theadClass}>
            {getHeaderGroups().map((headerGroup: any) => (
              <tr className={trClass} key={headerGroup.id}>
                {headerGroup.headers.map((header: any) => (
                  <th key={header.id} className={thClass}  {...{
                    onClick: header.column.getToggleSortingHandler(),
                  }}>
                    {header.isPlaceholder ? null : (
                      <React.Fragment>
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {{
                          asc: ' ',
                          desc: ' ',
                        }
                        [header.column.getIsSorted() as string] ?? null}
                        {header.column.getCanFilter() ? (
                          <div>
                            <Filter column={header.column} table={table} />
                          </div>
                        ) : null}
                      </React.Fragment>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody>
            {getRowModel().rows.length > 0 ? (
              getRowModel().rows.map((row: any) => {
                return (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell: any) => {
                      return (
                        <td key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={columns.length} className="text-center py-4">
                  <div className="d-flex flex-column align-items-center">
                    <i className="bx bx-info-circle fs-2 mb-2 text-muted"></i>
                    <p className="text-muted mb-0">
                      {globalFilter ? "No matching records found" : "No records available"}
                    </p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
          </Table>
        </div>
      </div>

      <Row className="align-items-center mt-2 g-3 text-center text-sm-start">
          <div className="col-sm">
            <div className="text-muted">
              {paginationInfo.totalEntries > 0 ? (
                <>
                  Showing <span className="fw-semibold">{paginationInfo.startEntry}</span> to <span className="fw-semibold">{paginationInfo.endEntry}</span> of <span className="fw-semibold">{paginationInfo.totalEntries}</span> Results
                </>
              ) : (
                <span className="fw-semibold">No Results</span>
              )}
            </div>
          </div>
          <div className="col-sm-auto">
            <ul className="pagination pagination-separated pagination-md justify-content-center justify-content-sm-start mb-0">
              <li className={!paginationInfo.canPrevious ? "page-item disabled" : "page-item"}>
                <Link to="#" className="page-link" onClick={handlePreviousPage}>Previous</Link>
              </li>
              {(() => {
                const totalPages = paginationInfo.pageOptions.length;
                const currentPage = paginationInfo.currentPageIndex;
                const delta = 1; // Number of pages to show before and after current page
                
                let pages = [];
                
                // Always show first page
                pages.push(0);
                
                // Calculate range around current page
                let rangeStart = Math.max(1, currentPage - delta);
                let rangeEnd = Math.min(totalPages - 2, currentPage + delta);
                
                // Adjust range if current page is near start or end
                if (currentPage <= delta) {
                  rangeEnd = Math.min(2 * delta + 1, totalPages - 2);
                }
                if (currentPage >= totalPages - delta - 1) {
                  rangeStart = Math.max(totalPages - 2 * delta - 2, 1);
                }
                
                // Add ellipsis after first page if needed
                if (rangeStart > 1) {
                  pages.push(-1); // -1 represents ellipsis
                }
                
                // Add pages in range
                for (let i = rangeStart; i <= rangeEnd; i++) {
                  pages.push(i);
                }
                
                // Add ellipsis before last page if needed
                if (rangeEnd < totalPages - 2) {
                  pages.push(-2); // -2 represents ellipsis
                }
                
                // Always show last page if we have more than one page
                if (totalPages > 1) {
                  pages.push(totalPages - 1);
                }
                
                return pages.map((pageIndex, key) => (
                  <React.Fragment key={key}>
                    {pageIndex >= 0 ? (
                      <li className="page-item">
                        <Link
                          to="#"
                          className={paginationInfo.currentPageIndex === pageIndex ? "page-link active" : "page-link"}
                          onClick={() => handlePageClick(pageIndex)}
                        >
                          {pageIndex + 1}
                        </Link>
                      </li>
                    ) : (
                      <li className="page-item disabled">
                        <span className="page-link">...</span>
                      </li>
                    )}
                  </React.Fragment>
                ));
              })()}
              <li className={!paginationInfo.canNext ? "page-item disabled" : "page-item"}>
                <Link to="#" className="page-link" onClick={handleNextPage}>Next</Link>
              </li>
            </ul>
          </div>
        </Row>
    </Fragment>
  );
};

export default TableContainer;